/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { styled as styled2, css as css2, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import Heading from '@atlaskit/heading';
import { colors, typography } from '@atlaskit/theme';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { LozengeAndAttributeValueModal } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/main.tsx';
import { MULTI_VALUE_ATTRIBUTE_THRESHOLD } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/index.tsx';
import { getBBRepoValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/bitbucket-repo/index.tsx';
import { getDefaultAttributeArrayValueFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { getGroupValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/group/index.tsx';
import { getOpsgenieTeamValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/opsgenie-team/index.tsx';
import { getProjectValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/project/index.tsx';
import { getObjectReferenceOptionsFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/referenced-object/index.tsx';
import { getUserValuesFromCmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/user/index.tsx';
import {
	DefaultAttributeSubtypeMap,
	AttributeTypeMap,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-types.tsx';
import type {
	CmdbReferencedObject,
	CmdbObjectAttributeValueDefault,
	CmdbObjectAttributeValueUser,
	CmdbObjectAttributeValueGroup,
	CmdbObjectAttributeValueStatus,
	CmdbObjectAttributeValueObject,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-values.tsx';
import type {
	CmdbObjectAttribute,
	CmdbObjectAttributeDefault,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type { CmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { BitbucketRepoAttribute } from './bitbucket-repo-attribute/index.tsx';
import { BooleanAttribute } from './boolean-attribute/index.tsx';
import { ObjectAttribute } from './button-attribute-with-avatar/index.tsx';
import { DateAttribute, DateTimeAttribute } from './date-and-time-attribute/index.tsx';
import { GroupAttribute } from './group-attribute/index.tsx';
import { OpsgenieTeamAttribute } from './opsgenie-team-attribute/index.tsx';
import { ProjectAttribute } from './project-attribute/index.tsx';
import { StatusAttribute } from './status-attribute/index.tsx';
import { TextAttribute, RichTextAttribute } from './text-attribute/index.tsx';
import { UnknownAttribute } from './unknown-attribute/index.tsx';
import { UrlAttribute, EmailAttribute } from './url-attribute/index.tsx';
import { UserAttribute } from './user-attribute/index.tsx';

export type Props = {
	attributes: CmdbObjectAttribute[];
	objectId: CmdbObjectId;
	onObjectAttributeClick: (arg1: CmdbReferencedObject) => void;
};

export const AttributesList = ({ attributes, objectId, onObjectAttributeClick }: Props) => {
	const renderDefaultAttribute = (attribute: CmdbObjectAttributeDefault) => {
		switch (attribute.objectTypeAttribute.defaultType.id.toString()) {
			case DefaultAttributeSubtypeMap.Boolean:
				return <BooleanAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.URL:
				return <UrlAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Email:
				return <EmailAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Textarea:
				return <RichTextAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Date:
				return <DateAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.DateTime:
				return <DateTimeAttribute attribute={attribute} />;
			case DefaultAttributeSubtypeMap.Select:
				return attribute.objectAttributeValues.length > MULTI_VALUE_ATTRIBUTE_THRESHOLD ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'select',
							attributeValues: getDefaultAttributeArrayValueFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.select-lozenge"
					/>
				) : (
					<TextAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.select-attribute"
					/>
				);
			case DefaultAttributeSubtypeMap.Text:
			case DefaultAttributeSubtypeMap.Integer:
			case DefaultAttributeSubtypeMap.Double:
			case DefaultAttributeSubtypeMap.IPAddress:
			case DefaultAttributeSubtypeMap.Time:
			default:
				return <TextAttribute attribute={attribute} />;
		}
	};

	const renderAttribute = (attribute: CmdbObjectAttribute) => {
		const showScalableLozengeButton =
			attribute.objectAttributeValues.length > MULTI_VALUE_ATTRIBUTE_THRESHOLD;
		switch (attribute.type) {
			case AttributeTypeMap.Default:
				return renderDefaultAttribute(attribute);
			case AttributeTypeMap.Group:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'group',
							attributeValues: getGroupValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.group-lozenge"
					/>
				) : (
					<GroupAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.group-attribute"
					/>
				);
			case AttributeTypeMap.Status:
				return <StatusAttribute attribute={attribute} />;
			case AttributeTypeMap.Object:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'object',
							attributeValues: getObjectReferenceOptionsFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.object-lozenge"
					/>
				) : (
					<ObjectAttribute
						attribute={attribute}
						onClick={onObjectAttributeClick}
						data-testid="servicedesk-insight-attribute-list.ui.object-attribute"
					/>
				);
			case AttributeTypeMap.User:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'user',
							attributeValues: getUserValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.user-lozenge"
					/>
				) : (
					<UserAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.user-attribute"
					/>
				);
			case AttributeTypeMap.Project:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'project',
							attributeValues: getProjectValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.project-lozenge"
					/>
				) : (
					<ProjectAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.project-attribute"
					/>
				);
			case AttributeTypeMap.BitbucketRepo:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'bitbucket',
							attributeValues: getBBRepoValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.bitbucket-lozenge"
					/>
				) : (
					<BitbucketRepoAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.bitbucket-attribute"
					/>
				);
			case AttributeTypeMap.OpsgenieTeam:
				return showScalableLozengeButton ? (
					<LozengeAndAttributeValueModal
						attributeValuesInfo={{
							type: 'opsgenieTeam',
							attributeValues: getOpsgenieTeamValuesFromCmdbObjectAttribute(attribute),
						}}
						attributeName={attribute.objectTypeAttribute.name ?? ''}
						data-testid="servicedesk-insight-attribute-list.ui.opsgenie-team-lozenge"
					/>
				) : (
					<OpsgenieTeamAttribute
						attribute={attribute}
						data-testid="servicedesk-insight-attribute-list.ui.opsgenie-team-attribute"
					/>
				);
			default:
				return <UnknownAttribute attribute={attribute} objectId={objectId} />;
		}
	};

	const isTextarea = (attribute: CmdbObjectAttribute) =>
		attribute.type === AttributeTypeMap.Default &&
		attribute.objectTypeAttribute.defaultType.id === DefaultAttributeSubtypeMap.Textarea;

	const objectAttributeValuesAreNotEmpty = (
		values:
			| CmdbObjectAttributeValueDefault[]
			| CmdbObjectAttributeValueUser[]
			| CmdbObjectAttributeValueGroup[]
			| CmdbObjectAttributeValueStatus[]
			| CmdbObjectAttributeValueObject[],
	): boolean => values.every((value) => Object.keys(value).length > 0);

	return (
		<ListWrapper>
			{attributes.map(
				(attribute) =>
					objectAttributeValuesAreNotEmpty(attribute.objectAttributeValues) && (
						<AttributeWrapper
							key={attribute.objectTypeAttributeId}
							isColumn={isTextarea(attribute)}
							data-testid="servicedesk-insight-attribute-list.ui.attribute-wrapper"
						>
							<AttributeLabel isColumn={isTextarea(attribute)}>
								{attribute.objectTypeAttribute.name}
							</AttributeLabel>
							<AttributeValue isColumn={isTextarea(attribute)}>
								{renderAttribute(attribute)}
							</AttributeValue>
						</AttributeWrapper>
					),
			)}
		</ListWrapper>
	);
};

const AttributeLabelNew = ({ isColumn, children }: { isColumn: boolean; children: ReactNode }) => (
	<AttributeLabelWrapper isColumn={isColumn}>
		<Heading size="xsmall" as="span">
			<span css={attributeLabelColour}>{children}</span>
		</Heading>
	</AttributeLabelWrapper>
);

const ATTRIBUTE_VERTICAL_GAP = gridSize; // Total vertical space between each attribute
const LABEL_VALUE_HORIZONTAL_GAP = gridSize * 3; // Total horizontal space between label & value

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ListWrapperOld = styled.div({
	/* Offset the outside padding from children */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `-${ATTRIBUTE_VERTICAL_GAP / 2}px -${LABEL_VALUE_HORIZONTAL_GAP / 2}px`,
});

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ListWrapperNew = styled2.div({
	/* Offset the outside padding from children */
	// Half vertical space between each attribute, half horizontal space between label & value
	margin: `${token('space.negative.050')} ${token('space.negative.150')}	`,
});

const ListWrapper = styledComponentWithCondition(
	() => fg('jsm_assets_-_convert_insight-attribute_to_compiled'),
	ListWrapperNew,
	ListWrapperOld,
);

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const AttributeLabelOld = styled.div`
	${typography.h400()};
	color: ${token('color.text.subtle', colors.N600)};
	margin: ${ATTRIBUTE_VERTICAL_GAP / 2}px ${LABEL_VALUE_HORIZONTAL_GAP / 2}px;
	min-height: ${gridSize * 3}px;
	line-height: ${gridSize * 3}px;
	flex-basis: ${gridSize * 14}px;
	flex-grow: 1;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage
const attributeLabelColour = css2({ color: token('color.text.subtle', colors.N600) });
// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const AttributeLabelWrapper = styled2.div<{ isColumn: boolean }>({
	// Half vertical space between each attribute, half horizontal space between label & value
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: ({ isColumn }) =>
		isColumn
			? `
	${token('space.050')} ${token('space.150')} ${token('space.100')} ${token('space.150')}
	`
			: `${token('space.050')} ${token('space.150')}`,
	minHeight: token('space.300'),
	lineHeight: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexBasis: ({ isColumn }) => (isColumn ? 'auto' : '112px'),
	flexGrow: 1,
});

const AttributeLabel = styledComponentWithCondition(
	() => fg('jsm_assets_-_convert_insight-attribute_to_compiled'),
	AttributeLabelNew,
	AttributeLabelOld,
);

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const AttributeValueOld = styled.div({
	overflowWrap: 'break-word',
	wordWrap: 'break-word',
	wordBreak: 'break-all',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `${ATTRIBUTE_VERTICAL_GAP / 2}px ${LABEL_VALUE_HORIZONTAL_GAP / 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 28}px`,
	flexGrow: 1,
});

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const AttributeValueNew = styled2.div<{ isColumn: boolean }>({
	overflowWrap: 'break-word',
	wordWrap: 'break-word',
	wordBreak: 'break-all',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	// Half vertical space between each attribute, half horizontal space between label & value
	margin: `${token('space.050')} ${token('space.150')}`,
	minHeight: token('space.300'),
	lineHeight: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexBasis: (props) => (props.isColumn ? 'auto' : '224px'),
	flexGrow: 1,
});

const AttributeValue = styledComponentWithCondition(
	() => fg('jsm_assets_-_convert_insight-attribute_to_compiled'),
	AttributeValueNew,
	AttributeValueOld,
);

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const AttributeWrapperOld = styled.div<{ isColumn: boolean }>(
	{
		display: 'flex',
		flexWrap: 'wrap',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isColumn }) =>
		isColumn
			? // eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css`
					flex-direction: column;

					${AttributeLabelOld} {
						flex-basis: auto;
						margin-bottom: ${token('space.100', '8px')};
					}

					${AttributeValueOld} {
						flex-basis: auto;
					}
				`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					flexDirection: 'row',
				}),
);

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const AttributeWrapperNew = styled2.div<{ isColumn: boolean }>(
	{
		display: 'flex',
		flexWrap: 'wrap',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isColumn }) =>
		isColumn
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css2({
					flexDirection: 'column',
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css2({
					flexDirection: 'row',
				}),
);

const AttributeWrapper = styledComponentWithCondition(
	() => fg('jsm_assets_-_convert_insight-attribute_to_compiled'),
	AttributeWrapperNew,
	AttributeWrapperOld,
);
